import { Divider, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  FlowDurationUnits,
  FlowFormBlockPracticeType,
  FlowFormBlockSchedulingConfigsState,
} from 'flow-schema';
import pluralize from 'pluralize';

import { Text, api, getQueryProps, isDefined, isTruthy } from 'common';

import { SchedulingConfigsPracticeBlock } from './Practice';
import { SchedulingConfigsProviderBlock } from './Provider';
import { SchedulingConfigsReasonForVisitBlock } from './ReasonForVisit';
import { SchedulingConfigsSeriesAppointmentBlock } from './SeriesAppointment';
import { useSchedulingConfigsField } from './hooks/useSchedulingConfigsField';
import { APPOINTMENT_KIND_TYPE_NAME } from '../../../../../../../OrchestratorInstance/common';
import { useFlowDefinitionContext } from '../../../../../context';
import { BlockCheckboxField } from '../../../ConfigFields/Checkbox';
import { BlockDurationField } from '../../../ConfigFields/Duration';
import { BlockSelectField } from '../../../ConfigFields/SelectField';
import { BlockSwitchField } from '../../../ConfigFields/Switch';
import { BlockTextField } from '../../../ConfigFields/TextField';
import { useSelectedNodeField } from '../../../ConfigFields/useSelectedNodeField';

interface SchedulingConfigsProps {
  fieldNamePrefix: string;
}

/**
 * A value only counts as interpolated if it is a complete {{...}} template.
 */
const BLOCK_VALUE_TEMPLATE_REGEX = /^{{(.*?)}}$/g;

export function SchedulingConfigsFormFields(props: SchedulingConfigsProps) {
  const { fieldNamePrefix } = props;
  const { value: schedulingConfigsValue, setValue: setSchedulingConfigsValue } =
    useSchedulingConfigsField(fieldNamePrefix);
  const { flowDefinition } = useFlowDefinitionContext();
  const { isFieldErrorVisible: isFhirAppointmentKindsErrorVisible } = useSelectedNodeField(
    `${fieldNamePrefix}.fhirAppointmentKinds`
  );

  const isAppointmentKindOptionsFilteredByPractices =
    schedulingConfigsValue?.practiceType === FlowFormBlockPracticeType.StaticList &&
    isTruthy(schedulingConfigsValue?.practiceIds?.length);
  const { data: appointmentKindOptions = [], isPending: appointmentKindOptionsIsPending } =
    useQuery({
      ...getQueryProps(api.client.Orchestrator.CanonicalField, 'findAll', {
        canonicalFieldTypeName: APPOINTMENT_KIND_TYPE_NAME,
        ...(isAppointmentKindOptionsFilteredByPractices
          ? { usedByPracticeIds: schedulingConfigsValue.practiceIds }
          : { usedByOrganizationId: flowDefinition.organizationId }),
        usedByInstanceKinds: [api.Orchestrator.OrchestratorInstance.OrchestratorInstanceKind.API],
      }),
      select: (canonicalFields) =>
        canonicalFields.map(({ value, name }) => ({
          value,
          label: name,
        })),
    });

  const currentAppointmentKind = schedulingConfigsValue?.fhirAppointmentKinds?.at(0);
  const invalidAppointmentKind =
    !isFhirAppointmentKindsErrorVisible &&
    !appointmentKindOptionsIsPending &&
    isTruthy(currentAppointmentKind) &&
    !isDefined(currentAppointmentKind?.match(BLOCK_VALUE_TEMPLATE_REGEX))
      ? schedulingConfigsValue?.fhirAppointmentKinds?.find(
          (selectedAppointmentKindValue) =>
            !appointmentKindOptions.some(
              (validAppointmentKind) => validAppointmentKind.value === selectedAppointmentKindValue
            )
        )
      : undefined;

  return (
    <Stack flex={1} gap={1}>
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Text variant="heading5" bold alignContent="center">
          Scheduling Configurations
        </Text>
        <BlockSwitchField
          fieldName={`${fieldNamePrefix}.schedulingConfigsState`}
          // FIXME: BlockSwitch component needs to have a label to conform to E2E test expectations and consistent design patterns
          label=""
          toggleStates={{
            activeState: FlowFormBlockSchedulingConfigsState.Enabled,
            inactiveState: FlowFormBlockSchedulingConfigsState.Disabled,
          }}
          onChange={(isEnabled) => {
            if (isEnabled && !isDefined(schedulingConfigsValue)) {
              setSchedulingConfigsValue({
                schedulingConfigsState: FlowFormBlockSchedulingConfigsState.Enabled,
              });
            }
          }}
        />
      </Stack>
      {schedulingConfigsValue?.schedulingConfigsState ===
      FlowFormBlockSchedulingConfigsState.Enabled ? (
        <Stack gap={1}>
          <SchedulingConfigsPracticeBlock fieldNamePrefix={fieldNamePrefix} />
          <BlockSelectField
            fieldName={`${fieldNamePrefix}.fhirAppointmentKinds.0`}
            label="Appointment Kind"
            fieldVariant="SelectOrBlockValueTemplate"
            onSelectOrBlockValueTemplateModeChange={() => {
              setSchedulingConfigsValue({ fhirAppointmentKinds: undefined });
            }}
            options={
              isTruthy(currentAppointmentKind) &&
              !appointmentKindOptions.some((option) => option.value === currentAppointmentKind)
                ? [
                    ...appointmentKindOptions,
                    {
                      label: currentAppointmentKind,
                      value: currentAppointmentKind,
                      disabled: true,
                    },
                  ]
                : appointmentKindOptions
            }
            onClear={() => {
              setSchedulingConfigsValue({ fhirAppointmentKinds: undefined });
            }}
            errorMessage={
              isDefined(invalidAppointmentKind)
                ? `${invalidAppointmentKind} is not configured for the ${
                    isAppointmentKindOptionsFilteredByPractices
                      ? pluralize('selected practice', schedulingConfigsValue?.practiceIds?.length)
                      : 'organization'
                  }.`
                : undefined
            }
          />
          <SchedulingConfigsProviderBlock fieldNamePrefix={fieldNamePrefix} />
          <BlockTextField
            fieldName={`${fieldNamePrefix}.fhirTotalResult`}
            label="Max # of appointments"
            type="number"
          />
          <BlockSelectField
            fieldName={`${fieldNamePrefix}.scheduleAppointmentDisplayBy`}
            label="Group appointments by"
            defaultValue={api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy.Practice}
            options={[
              {
                label: 'Practice',
                value: api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy.Practice,
              },
              {
                label: 'Provider',
                value: api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy.Provider,
              },
            ]}
          />
          <BlockSelectField
            fieldName={`${fieldNamePrefix}.scheduleAppointmentSortBy`}
            label="Default sorting behavior"
            defaultValue={
              api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentSortBy.Availability
            }
            options={[
              {
                label: 'Alphabetically',
                value: api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentSortBy.Alphabetically,
              },
              {
                label: 'Availability',
                value: api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentSortBy.Availability,
              },
              {
                label: 'Distance',
                value: api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentSortBy.Distance,
              },
            ]}
          />
          <BlockDurationField
            variant="BlockValueTemplate"
            fieldName={`${fieldNamePrefix}.scheduleAppointmentStartTime`}
            allowedUnits={[
              FlowDurationUnits.Minutes,
              FlowDurationUnits.Hours,
              FlowDurationUnits.Days,
            ]}
            valueLabel="Default Start Time"
            unitFieldNameSuffix="units"
            defaultUnit={FlowDurationUnits.Minutes}
            valueHelperText="Hide slots before this amount of time"
            toggleLabel="Enable Default Start Time"
            required={false}
          />
          <BlockDurationField
            variant="BlockValueTemplate"
            fieldName={`${fieldNamePrefix}.scheduleAppointmentEndTime`}
            allowedUnits={[
              FlowDurationUnits.Minutes,
              FlowDurationUnits.Hours,
              FlowDurationUnits.Days,
            ]}
            valueLabel="Default End Time"
            unitFieldNameSuffix="units"
            defaultUnit={FlowDurationUnits.Minutes}
            valueHelperText="Hide slots after this amount of time"
            toggleLabel="Enable Default End Time"
            required={false}
          />
          <Stack gap={1}>
            <Text variant="heading5">Display & Behavior</Text>
            <BlockCheckboxField
              fieldName={`${fieldNamePrefix}.scheduleAppointmentCallSectionEnabled`}
              label="Display call section"
            />
            <BlockCheckboxField
              fieldName={`${fieldNamePrefix}.customConfigs.scheduleNextAvailableAppointmentEnabled`}
              label="Schedule next available appointment"
            />
          </Stack>
          <Divider sx={{ marginY: 1.5 }} />
          <SchedulingConfigsReasonForVisitBlock fieldNamePrefix={fieldNamePrefix} />
          <Divider sx={{ marginY: 1.5 }} />
          <SchedulingConfigsSeriesAppointmentBlock fieldNamePrefix={fieldNamePrefix} />
        </Stack>
      ) : null}
    </Stack>
  );
}
